import type { FunctionComponent } from "react"

import { Divider } from "@jobteaser/spark/components/Divider"
import { ExternalLink } from "@jobteaser/spark/components/Icons/ExternalLink"
import { Text } from "@jobteaser/spark/components/Text"
import IconOrganization from "@jobteaser/ui-kit/assets/icons/jds_organization.svg"
import IconPin from "@jobteaser/ui-kit/assets/icons/jds_pin.svg"
import IconWork from "@jobteaser/ui-kit/assets/icons/jds_work.svg"
import { Tooltip } from "@jobteaser/ui-kit/components/Tooltip/Tooltip"

import { KeyInformation } from "@/features/companyDetail/components/shared/KeyInformation/KeyInformation"
import { BUSINESS_TYPES, BUSINESS_TYPES_I18N } from "@/modules/companies/constants/enums"
import { useCompanyExternalLinkTrack } from "@/modules/companies/hooks/tracking/useCompanyExternalLinkTrack"
import { useTranslation } from "@/modules/i18n/components/useTranslation"

import styles from "./HeaderKeyInformation.module.css"

type HeaderKeyInformationProps = {
  businessType?: string
  headquarterAddress?: string
  sector?: string
}

export const HeaderKeyInformation: FunctionComponent<HeaderKeyInformationProps> = ({
  businessType,
  headquarterAddress,
  sector,
}) => {
  const { t } = useTranslation(["companies_client", "shared_business_types", "shared_company_categories"])
  const companyExternalLinkTrack = useCompanyExternalLinkTrack()

  return (
    <div className={styles.headerKeyInformation}>
      {!!businessType && businessType !== BUSINESS_TYPES.COMPANY_PAGE_BUSINESS_TYPE_UNSPECIFIED && (
        <>
          <KeyInformation
            className={styles.headerKeyInformation__keyInformation}
            colorReverse
            icon={IconOrganization}
            noSpacing
            title={t("companies_client.header.companyType")}
          >
            <Text data-testid="company_header_business_type" variant="body1" weight="semiBold">
              {t(`shared_business_types.${BUSINESS_TYPES_I18N[businessType]}`)}
            </Text>
          </KeyInformation>
          <Divider className={styles.headerKeyInformation__separator} onBackground="dark" orientation="horizontal" />
        </>
      )}
      {!!sector && (
        <>
          <KeyInformation
            className={styles.headerKeyInformation__keyInformation}
            colorReverse
            icon={IconWork}
            noSpacing
            title={t("companies_client.header.sector")}
          >
            <Text data-testid="company_header_sector" variant="body1" weight="semiBold">
              {t(`shared_company_categories.${sector}`)}
            </Text>
          </KeyInformation>
          {headquarterAddress && (
            <Divider className={styles.headerKeyInformation__separator} onBackground="dark" orientation="horizontal" />
          )}
        </>
      )}
      {!!headquarterAddress && (
        <KeyInformation
          className={styles.headerKeyInformation__keyInformation}
          colorReverse
          icon={IconPin}
          noSpacing
          title={t("companies_client.header.localisation")}
        >
          <div className={styles.headerKeyInformation__headquarter_address}>
            <Tooltip label={headquarterAddress} tooltipClassName={styles.headerKeyInformation__tooltip}>
              <Text
                className={styles.headerKeyInformation__localization}
                data-testid="company_header_address"
                variant="body1"
                weight="semiBold"
              >
                {headquarterAddress}
              </Text>
            </Tooltip>
            <a
              aria-label={`Google maps ${t("companies_client.link.target_blank")}`}
              className={styles.headerKeyInformation__localization_link}
              href={`http://maps.google.com/?q=${headquarterAddress}`}
              onClick={() => companyExternalLinkTrack(`http://maps.google.com/?q=${headquarterAddress}`, "googlemaps")}
              rel="noopener noreferrer"
              target="_blank"
            >
              <ExternalLink />
            </a>
          </div>
        </KeyInformation>
      )}
    </div>
  )
}
